import React, { createContext, useEffect, useState, useCallback } from 'react';
import { 
  PublicClientApplication, 
  InteractionRequiredAuthError
} from '@azure/msal-browser';
import type { 
  AccountInfo,
  SilentRequest
} from '@azure/msal-browser';
import { msalConfig, loginRequest, apiConfig } from '@/config/authConfig';

interface AuthContextType {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  isLoading: boolean;
  error: Error | null;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const msalInstance = new PublicClientApplication(msalConfig);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tokenRefreshTimer, setTokenRefreshTimer] = useState<NodeJS.Timeout | null>(null);
  // Track interaction status if needed later
  // const [inProgress, setInProgress] = useState<InteractionStatus>(InteractionStatus.None);

  // Decode JWT to get expiration time
  const getTokenExpiration = (token: string): number | null => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  // Schedule proactive token refresh before expiration
  const scheduleTokenRefresh = useCallback(async (token: string) => {
    const expirationTime = getTokenExpiration(token);
    if (!expirationTime) return;

    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;

    // Refresh 5 minutes before expiration (or halfway through if token is short-lived)
    const refreshTime = Math.max(timeUntilExpiration - 5 * 60 * 1000, timeUntilExpiration / 2);

    // Clear any existing timer
    if (tokenRefreshTimer) {
      clearTimeout(tokenRefreshTimer);
    }

    if (refreshTime > 0) {
      const timer = setTimeout(async () => {
        console.log('Proactively refreshing access token before expiration');
        // We intentionally don't include getAccessToken in dependencies to avoid infinite loops
        // The function uses msalInstance which is stable across renders
        await getAccessToken();
      }, refreshTime);

      setTokenRefreshTimer(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenRefreshTimer]);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();

        // Handle redirect promise
        await msalInstance.handleRedirectPromise()
          .then((response) => {
            if (response && response.account) {
              setUser(response.account);
              setIsAuthenticated(true);
              msalInstance.setActiveAccount(response.account);
            }
          })
          .catch((error) => {
            console.error('Error handling redirect:', error);
            setError(error);
          });

        // Check if user is already signed in
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          setUser(accounts[0]);
          setIsAuthenticated(true);
          msalInstance.setActiveAccount(accounts[0]);

          // Get initial token and schedule proactive refresh
          try {
            const silentRequest: SilentRequest = {
              scopes: apiConfig.scopes,
              account: accounts[0],
            };
            const tokenResponse = await msalInstance.acquireTokenSilent(silentRequest);
            await scheduleTokenRefresh(tokenResponse.accessToken);
          } catch (tokenError) {
            console.error('Error acquiring initial token:', tokenError);
          }
        }
      } catch (error) {
        console.error('Error initializing MSAL:', error);
        setError(error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeMsal();
  }, [scheduleTokenRefresh]);

  const login = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await msalInstance.loginPopup(loginRequest);
      if (response.account) {
        setUser(response.account);
        setIsAuthenticated(true);
        msalInstance.setActiveAccount(response.account);

        // Schedule proactive token refresh after login
        if (response.accessToken) {
          await scheduleTokenRefresh(response.accessToken);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error as Error);

      // Fallback to redirect if popup blocked
      if (error instanceof Error && error.message.includes('popup')) {
        try {
          await msalInstance.loginRedirect(loginRequest);
        } catch (redirectError) {
          console.error('Redirect login error:', redirectError);
          setError(redirectError as Error);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);

    // Clear token refresh timer on logout
    if (tokenRefreshTimer) {
      clearTimeout(tokenRefreshTimer);
      setTokenRefreshTimer(null);
    }

    try {
      const account = msalInstance.getActiveAccount();
      if (account) {
        await msalInstance.logoutPopup({
          account: account,
        });
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Logout error:', error);

      // Fallback to redirect if popup blocked
      try {
        const account = msalInstance.getActiveAccount();
        await msalInstance.logoutRedirect({
          account: account,
        });
      } catch (redirectError) {
        console.error('Redirect logout error:', redirectError);
        setError(redirectError as Error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    try {
      const account = msalInstance.getActiveAccount();
      if (!account) {
        throw new Error('No active account');
      }

      const silentRequest: SilentRequest = {
        scopes: apiConfig.scopes,
        account: account,
      };

      // Try to acquire token silently
      try {
        const response = await msalInstance.acquireTokenSilent(silentRequest);
        // Schedule proactive refresh for the new token
        await scheduleTokenRefresh(response.accessToken);
        return response.accessToken;
      } catch (silentError) {
        // If silent acquisition fails, try interactive
        if (silentError instanceof InteractionRequiredAuthError) {
          const response = await msalInstance.acquireTokenPopup(silentRequest);
          // Schedule proactive refresh for the new token
          await scheduleTokenRefresh(response.accessToken);
          return response.accessToken;
        }
        throw silentError;
      }
    } catch (error) {
      console.error('Error acquiring access token:', error);
      setError(error as Error);
      return null;
    }
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (tokenRefreshTimer) {
        clearTimeout(tokenRefreshTimer);
      }
    };
  }, [tokenRefreshTimer]);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    login,
    logout,
    getAccessToken,
    isLoading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};